import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { classNames } from '../../utils/classNames';
import { Button } from './Button';

export const MODAL_BODY_CLASS = 'bg-slate-50/75 dark:bg-slate-950/35';

export const Modal = ({
  title,
  open,
  onClose,
  closeDisabled = false,
  children,
  leadingSlot,
  overlayClassName,
  panelClassName,
  contentClassName,
  titleClassName,
  closeButtonClassName,
  closeIconClassName,
  hideHeader = false,
  labelledBy
}) => {
  const titleId = useId();
  const overlayRef = useRef(null);
  const panelRef = useRef(null);
  const headingId = labelledBy || (title ? titleId : undefined);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousCount = Number(document.body.dataset.modalCount || 0);
    document.body.dataset.modalCount = String(previousCount + 1);
    document.body.style.overflow = 'hidden';

    return () => {
      const nextCount = Math.max(0, Number(document.body.dataset.modalCount || 1) - 1);
      document.body.dataset.modalCount = String(nextCount);
      if (nextCount === 0) {
        document.body.style.overflow = '';
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open || closeDisabled || !onClose) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key !== 'Escape') {
        return;
      }

      const overlays = document.querySelectorAll('[data-risman-modal]');
      if (overlays.length && overlays[overlays.length - 1] !== overlayRef.current) {
        return;
      }

      event.preventDefault();
      onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeDisabled, onClose]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const panel = panelRef.current;
    if (!panel) {
      return undefined;
    }

    const previouslyFocused = document.activeElement;
    const getFocusable = () =>
      Array.from(
        panel.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true');

    const focusable = getFocusable();
    (focusable[0] || panel).focus();

    const onKeyDown = (event) => {
      if (event.key !== 'Tab') {
        return;
      }

      const items = getFocusable();
      if (items.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    panel.addEventListener('keydown', onKeyDown);
    return () => {
      panel.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, [open]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      ref={overlayRef}
      data-risman-modal=""
      className={classNames(
        'fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/50 p-4',
        overlayClassName
      )}
      onClick={() => {
        if (!closeDisabled && onClose) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        ref={panelRef}
        className={classNames(
          'glass-panel animate-floatin w-full max-w-2xl rounded-xl border border-border p-2 motion-reduce:animate-none',
          MODAL_BODY_CLASS,
          panelClassName
        )}
        onClick={(event) => event.stopPropagation()}
      >
        {leadingSlot}
        {hideHeader ? (
          title ? (
            <h2 id={titleId} className="sr-only">
              {title}
            </h2>
          ) : null
        ) : (
          <div className={classNames('mb-2 flex items-center justify-between gap-3', contentClassName)}>
            <h2 id={titleId} className={classNames('text-xl font-semibold', titleClassName)}>
              {title}
            </h2>
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={closeDisabled}
              aria-label="Close"
              className={classNames('h-10 w-10 rounded-lg p-0 text-xl', closeButtonClassName)}
            >
              <span className={classNames('leading-none', closeIconClassName)}>×</span>
            </Button>
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
};
